/**
 * DiagnosisLibraryDialog component - Category-organized dimension library browser
 *
 * Modal dialog that shows all available dimensions from the DIAGNOSIS_LIBRARY,
 * organized by category, with multi-select and add action.
 *
 * Per user decision: "Everything should be expandable/inspectable (no black boxes)"
 * — dimensions show instructions and output config in the dialog.
 */

import { ChevronDown, ChevronRight, Library, X } from 'lucide-react';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/Button';
import {
  DIAGNOSIS_CATEGORIES,
  DIAGNOSIS_LIBRARY,
} from '@/constants/diagnosis-library';
import { cn } from '@/lib/utils';
import type { DiagnosisDimension, DiagnosisOutputConfig } from '@/types/template';

// ============================================================================
// TYPES
// ============================================================================

export interface DiagnosisLibraryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (dims: DiagnosisDimension[]) => void;
  existingDimensionIds: string[];
}

// ============================================================================
// HELPERS
// ============================================================================

function outputTypeBadge(config: DiagnosisOutputConfig): string {
  switch (config.type) {
    case 'binary':
      return 'Binary';
    case 'scale':
      return `Scale ${config.scaleMin ?? 1}-${config.scaleMax ?? 5}`;
    case 'options':
      return `Options${config.allowMultiple ? ' (multi)' : ''}`;
  }
}

// ============================================================================
// DIMENSION ITEM (in library)
// ============================================================================

interface LibraryDimensionItemProps {
  dimension: DiagnosisDimension;
  isSelected: boolean;
  onToggle: (dimensionId: string) => void;
}

function LibraryDimensionItem({
  dimension,
  isSelected,
  onToggle,
}: LibraryDimensionItemProps) {
  const [inspecting, setInspecting] = useState(false);

  return (
    <div
      className={cn(
        'rounded-lg border p-3 transition-all cursor-pointer',
        isSelected
          ? 'border-brand bg-brand/5'
          : 'border-border-subtle hover:border-brand/30',
      )}
      onClick={() => onToggle(dimension.dimensionId)}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <div
          className={cn(
            'flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 transition-colors',
            isSelected
              ? 'border-brand bg-brand'
              : 'border-border-default bg-surface',
          )}
        >
          {isSelected && (
            <svg
              className="w-3 h-3 text-inverse"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-primary">
              {dimension.name}
            </span>
            <span className="bg-surface-subtle text-secondary text-[10px] font-medium px-1.5 py-0.5 rounded">
              {outputTypeBadge(dimension.outputConfig)}
            </span>
            {dimension.mediaTypes && dimension.mediaTypes.length === 1 && (
              <span className="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-[10px] font-medium px-1.5 py-0.5 rounded">
                {dimension.mediaTypes[0]} only
              </span>
            )}
          </div>

          {/* Truncated instructions as description */}
          <p className="text-xs text-muted mt-1 line-clamp-2 leading-relaxed">
            {dimension.instructions}
          </p>

          {/* Inspect toggle */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setInspecting((prev) => !prev);
            }}
            className="mt-2 flex items-center gap-1 text-[10px] font-medium text-brand hover:text-brand-hover transition-colors"
          >
            {inspecting ? (
              <>
                <ChevronDown size={10} />
                Hide details
              </>
            ) : (
              <>
                <ChevronRight size={10} />
                Inspect dimension
              </>
            )}
          </button>

          {/* Inspection panel */}
          {inspecting && (
            <div className="mt-3 space-y-3 border-t border-border-subtle pt-3" onClick={(e) => e.stopPropagation()}>
              {/* Full instructions */}
              <div>
                <label className="text-[10px] font-semibold text-secondary uppercase tracking-wide">
                  Instructions
                </label>
                <p className="text-xs text-primary mt-1 leading-relaxed whitespace-pre-wrap">
                  {dimension.instructions}
                </p>
              </div>

              {/* Video instructions */}
              {dimension.videoInstructions && (
                <div>
                  <label className="text-[10px] font-semibold text-secondary uppercase tracking-wide">
                    Video-Specific
                  </label>
                  <p className="text-xs text-primary mt-1 leading-relaxed whitespace-pre-wrap">
                    {dimension.videoInstructions}
                  </p>
                </div>
              )}

              {/* Image instructions */}
              {dimension.imageInstructions && (
                <div>
                  <label className="text-[10px] font-semibold text-secondary uppercase tracking-wide">
                    Image-Specific
                  </label>
                  <p className="text-xs text-primary mt-1 leading-relaxed whitespace-pre-wrap">
                    {dimension.imageInstructions}
                  </p>
                </div>
              )}

              {/* Output config */}
              <div>
                <label className="text-[10px] font-semibold text-secondary uppercase tracking-wide">
                  Output Config
                </label>
                <div className="mt-1 text-xs text-primary">
                  <span>Type: {dimension.outputConfig.type}</span>
                  {dimension.outputConfig.type === 'scale' && (
                    <span className="ml-2">
                      Range: {dimension.outputConfig.scaleMin}-{dimension.outputConfig.scaleMax}
                    </span>
                  )}
                  {dimension.outputConfig.options && (
                    <span className="ml-2">
                      Options: {dimension.outputConfig.options.join(', ')}
                    </span>
                  )}
                  {dimension.outputConfig.allowMultiple && (
                    <span className="ml-2">(multiple)</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CATEGORY SECTION
// ============================================================================

interface UnavailableDimension {
  dimension: DiagnosisDimension;
  reason: string;
}

interface CategorySectionProps {
  categoryKey: string;
  categoryName: string;
  categoryDescription: string;
  available: DiagnosisDimension[];
  unavailable: UnavailableDimension[];
  selectedIds: Set<string>;
  onToggle: (dimensionId: string) => void;
}

function CategorySection({
  categoryKey: _categoryKey,
  categoryName,
  categoryDescription,
  available,
  unavailable,
  selectedIds,
  onToggle,
}: CategorySectionProps) {
  const [collapsed, setCollapsed] = useState(false);
  const totalCount = available.length + unavailable.length;

  return (
    <div className="space-y-2">
      {/* Category header */}
      <button
        type="button"
        onClick={() => setCollapsed((prev) => !prev)}
        className="w-full flex items-center gap-2 text-left group"
      >
        {collapsed ? (
          <ChevronRight size={14} className="text-muted" />
        ) : (
          <ChevronDown size={14} className="text-muted" />
        )}
        <span className="text-sm font-semibold text-primary">{categoryName}</span>
        <span className="text-xs text-muted">
          ({available.length}{totalCount > available.length ? ` of ${totalCount}` : ''})
        </span>
      </button>
      <p className="text-xs text-muted ml-5 -mt-1">{categoryDescription}</p>

      {/* Dimensions */}
      {!collapsed && (
        <div className="space-y-2 ml-5">
          {/* Available dimensions */}
          {available.map((dim) => (
            <LibraryDimensionItem
              key={dim.dimensionId}
              dimension={dim}
              isSelected={selectedIds.has(dim.dimensionId)}
              onToggle={onToggle}
            />
          ))}

          {/* Unavailable dimensions (greyed out) */}
          {unavailable.map(({ dimension: dim, reason }) => (
            <div
              key={dim.dimensionId}
              className="rounded-lg border border-border-subtle p-3 opacity-40 pointer-events-none"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-primary">
                      {dim.name}
                    </span>
                    <span className="bg-surface-subtle text-secondary text-[10px] font-medium px-1.5 py-0.5 rounded">
                      {outputTypeBadge(dim.outputConfig)}
                    </span>
                    <span className="text-muted text-[10px] font-medium px-1.5 py-0.5 rounded bg-surface-subtle">
                      {reason}
                    </span>
                  </div>
                  <p className="text-xs text-muted mt-1 line-clamp-1 leading-relaxed">
                    {dim.instructions}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* All dimensions added message */}
          {available.length === 0 && unavailable.length === 0 && (
            <p className="text-xs text-muted italic py-3">
              All dimensions from this category have been added.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN DIALOG
// ============================================================================

export function DiagnosisLibraryDialog({
  open,
  onOpenChange,
  onAdd,
  existingDimensionIds,
}: DiagnosisLibraryDialogProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Build available and unavailable dimensions by category
  // Unified templates: all dimensions are selectable (no media-type filtering)
  const categorizedDimensions = useMemo(() => {
    const existingSet = new Set(existingDimensionIds);
    return DIAGNOSIS_CATEGORIES.map((cat) => {
      const allDims = DIAGNOSIS_LIBRARY[cat.key] || [];
      const available: DiagnosisDimension[] = [];
      const unavailable: UnavailableDimension[] = [];

      for (const dim of allDims) {
        if (existingSet.has(dim.dimensionId)) {
          unavailable.push({ dimension: dim, reason: 'Already added' });
        } else {
          available.push(dim);
        }
      }

      return { ...cat, available, unavailable };
    });
  }, [existingDimensionIds]);

  const totalAvailable = categorizedDimensions.reduce(
    (sum, cat) => sum + cat.available.length,
    0,
  );

  const handleToggle = useCallback((dimensionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(dimensionId)) {
        next.delete(dimensionId);
      } else {
        next.add(dimensionId);
      }
      return next;
    });
  }, []);

  const handleAdd = useCallback(() => {
    // Collect selected dimensions from the library
    const dimsToAdd: DiagnosisDimension[] = [];
    for (const cat of categorizedDimensions) {
      for (const dim of cat.available) {
        if (selectedIds.has(dim.dimensionId)) {
          dimsToAdd.push({ ...dim, outputConfig: { ...dim.outputConfig } });
        }
      }
    }
    if (dimsToAdd.length > 0) {
      onAdd(dimsToAdd);
    }
    setSelectedIds(new Set());
    onOpenChange(false);
  }, [categorizedDimensions, selectedIds, onAdd, onOpenChange]);

  const handleClose = useCallback(() => {
    setSelectedIds(new Set());
    onOpenChange(false);
  }, [onOpenChange]);

  // Handle escape key
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, handleClose]);

  if (!open) return null;

  const content = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="library-dialog-title"
        className={cn(
          'w-full max-w-2xl max-h-[80vh] bg-surface rounded-xl shadow-xl border border-border-subtle',
          'animate-in zoom-in-95 slide-in-from-bottom-2 duration-200',
          'flex flex-col',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-brand/10 text-brand">
              <Library size={16} />
            </div>
            <div>
              <h2 id="library-dialog-title" className="text-lg font-semibold text-primary">
                Dimension Library
              </h2>
              <p className="text-xs text-muted">
                {totalAvailable} dimension{totalAvailable !== 1 ? 's' : ''} available
                {selectedIds.size > 0 && (
                  <span className="text-brand font-medium ml-1">
                    ({selectedIds.size} selected)
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-md text-muted hover:text-primary hover:bg-surface-subtle transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content (scrollable) */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {totalAvailable === 0 && categorizedDimensions.every((c) => c.unavailable.length === 0) ? (
            <div className="text-center py-12">
              <p className="text-sm text-muted">
                All available dimensions have already been added to this template.
              </p>
            </div>
          ) : (
            categorizedDimensions.map((cat) => (
              <CategorySection
                key={cat.key}
                categoryKey={cat.key}
                categoryName={cat.name}
                categoryDescription={cat.description}
                available={cat.available}
                unavailable={cat.unavailable}
                selectedIds={selectedIds}
                onToggle={handleToggle}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border-subtle flex-shrink-0">
          <p className="text-xs text-muted">
            Click on a dimension to select it, then click &quot;Add Selected&quot;
          </p>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={selectedIds.size === 0}
            >
              Add Selected ({selectedIds.size})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
